import { ref, readonly } from "vue";
import duration, { type Duration } from "dayjs/plugin/duration";
import { useDayjs } from "@sazka/utils/dayjs";
import { DateTimeUtils } from "@sazka/utils/date-time-utils";
import { Lottery } from "@sazka/lotteries/js/models/lottery";

const { dayjs } = useDayjs();
dayjs.extend<Duration>(duration);

let intervalId: ReturnType<typeof setInterval> = null;

export function useProductPageLotteryCountdowns(lottery: Lottery) {
    const formattedTime = ref("");
    const QUICK_LOTTERIES = ["keno", "rychle-kacky", "kameny", "rychla-6", "vsechno-nebo-nic", "power-spin"];
    const HOUR_LOTTERIES = ["stastnych-10", "kasicka", "stastne-datum", "mini-renta"];
    const HOUR_IN_SECONDS = 3600;

    function formatQuickLotteryCountdown(totalSeconds: number, shouldShowShortForm = false) {
        const time = dayjs.duration(totalSeconds * 1000);
        const minutes = time.minutes();
        const seconds = time.seconds();
        const minutesLabel = DateTimeUtils.getCountdownUnitLabel("minutes", minutes);
        const secondsLabel = DateTimeUtils.getCountdownUnitLabel("seconds", seconds);

        if (shouldShowShortForm) {
            formattedTime.value = `${minutes}:${seconds.toLocaleString(undefined, {
                minimumIntegerDigits: 2,
            })}`;
        } else {
            formattedTime.value = `<strong>${minutes}</strong> ${minutesLabel} <strong>${seconds}</strong> ${secondsLabel}`;
        }
    }

    function formatHourLotteryCountdown(seconds: number, shouldShowShortForm = false) {
        const time = dayjs.duration(seconds * 1000);
        const hours = time.hours();
        const minutes = time.minutes();
        const hoursLabel = DateTimeUtils.getCountdownUnitLabel("hours", hours);
        const minutesLabel = DateTimeUtils.getCountdownUnitLabel("minutes", minutes);

        if (shouldShowShortForm) {
            formattedTime.value = `${hours} ${hoursLabel}`;
        } else {
            formattedTime.value = `<strong>${hours}</strong> ${hoursLabel} <strong>${minutes}</strong> ${minutesLabel}`;
        }
    }

    function formatStandardLotteryCountdown(seconds: number, shouldShowShortForm = false) {
        const time = dayjs.duration(seconds * 1000);
        const days = time.days();
        const hours = time.hours();
        const minutes = time.minutes();

        const daysLabel = DateTimeUtils.getCountdownUnitLabel("days", days);
        const hoursLabel = DateTimeUtils.getCountdownUnitLabel("hours", hours);
        const minutesLabel = DateTimeUtils.getCountdownUnitLabel("minutes", minutes);

        if (shouldShowShortForm) {
            if (days > 0) {
                formattedTime.value = `${days} ${daysLabel}`;
            } else if (hours > 0) {
                formattedTime.value = `${hours} ${hoursLabel}`;
            } else {
                formattedTime.value = `${minutes} ${minutesLabel}`;
            }
        } else {
            formattedTime.value = `<strong>${days}</strong> ${daysLabel} <strong>${hours}</strong> ${hoursLabel} <strong>${minutes}</strong> ${minutesLabel}`;
        }
    }

    function formatLotteryCountdown(seconds: number, shouldShowShortForm = false) {
        if (QUICK_LOTTERIES.includes(lottery)) {
            formatQuickLotteryCountdown(seconds, shouldShowShortForm);
        } else if (HOUR_LOTTERIES.includes(lottery) && seconds > HOUR_IN_SECONDS) {
            formatHourLotteryCountdown(seconds, shouldShowShortForm);
        } else {
            formatStandardLotteryCountdown(seconds, shouldShowShortForm);
        }
    }

    function startCountdown(cb: () => void) {
        intervalId = setInterval(cb, 1000);
    }

    function killCountdown() {
        clearInterval(intervalId);
        intervalId = null;
    }

    return {
        formatLotteryCountdown,
        startCountdown,
        killCountdown,
        formattedTime: readonly(formattedTime),
    };
}
